import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { deleteAPI, getAPI, postAPI, putAPI } from "./Networking";
import {
  GetCityParams,
  GetCountryParams,
  GetStateParams,
} from "../models/Address/getAddressParams";
import {
  Address,
  AddressRequestBody,
  City,
  Country,
  State,
} from "../models/Address/Address";
import { AddressFilters } from "../models/Address/AddressFilters";

export default class AddressAPI {
  getAddress = async (
    filter: AddressFilters
  ): Promise<PaginatedAPIResponse<Address>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filter.page_no) {
        queryParams.append("page_no", filter.page_no.toString());
      }
      if (filter.limit) queryParams.append("limit", filter.limit.toString());

      if (filter.search_text) {
        queryParams.append("search_text", filter.search_text);
      }

      if (filter.customer_id) {
        queryParams.append("customer_id", filter.customer_id.toString());
      }

      if (filter.sort_by) queryParams.append("sort_by", filter.sort_by);
      if (filter.sort_type)
        queryParams.append("sort_type", filter.sort_type.toString());
      queryParams.append("active", filter.active === false ? "false" : "true");

      const response = await getAPI(
        `/v5/address/getAddress?${queryParams.toString()}`
      );

      return response.result as PaginatedAPIResponse<Address>;
    } catch (error) {
      throw error;
    }
  };

  updateAddressDetails = async (
    addressDetails: AddressRequestBody
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/address`, addressDetails);
      return response;
    } catch (error) {
      throw error;
    }
  };

  createAddress = async (
    addressDetails: AddressRequestBody
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/address`, addressDetails);
      return response;
    } catch (error) {
      throw error;
    }
  };

  deleteAddress = async (id: number): Promise<APIResponse> => {
    try {
      const response = await deleteAPI(`/v5/address/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getCity = async (
    filter: GetCityParams
  ): Promise<PaginatedAPIResponse<City>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filter.page_no) {
        queryParams.append("page_no", filter.page_no.toString());
      }

      if (filter.country_id)
        queryParams.append("country_id", filter.country_id.toString());
      if (filter.state_id)
        queryParams.append("state_id", filter.state_id.toString());
      if (filter.limit) queryParams.append("limit", filter.limit.toString());

      if (filter.search_text) {
        queryParams.append("search_text", filter.search_text);
      }

      if (filter.sort_by) queryParams.append("sort_by", filter.sort_by);
      if (filter.sort_type)
        queryParams.append("sort_type", filter.sort_type.toString());
      queryParams.append("active", filter.active === false ? "false" : "true");

      const response = await getAPI(`/v5/city?${queryParams.toString()}`);

      return response.result as PaginatedAPIResponse<City>;
    } catch (error) {
      throw error;
    }
  };

  getCountry = async (
    filter: GetCountryParams
  ): Promise<PaginatedAPIResponse<Country>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filter.page_no) {
        queryParams.append("page_no", filter.page_no.toString());
      }
      if (filter.limit) queryParams.append("limit", filter.limit.toString());

      if (filter.search_text) {
        queryParams.append("search_text", filter.search_text);
      }

      if (filter.sort_by) queryParams.append("sort_by", filter.sort_by);
      if (filter.sort_type)
        queryParams.append("sort_type", filter.sort_type.toString());
      queryParams.append("active", filter.active === false ? "false" : "true");

      const response = await getAPI(
        `/v5/country/list?${queryParams.toString()}`
      );

      return response.result as PaginatedAPIResponse<Country>;
    } catch (error) {
      throw error;
    }
  };

  getState = async (
    filter: GetStateParams
  ): Promise<PaginatedAPIResponse<State>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filter.page_no) {
        queryParams.append("page_no", filter.page_no.toString());
      }

      if (filter.country_id)
        queryParams.append("country_id", filter.country_id.toString());
      if (filter.limit) queryParams.append("limit", filter.limit.toString());

      if (filter.search_text) {
        queryParams.append("search_text", filter.search_text);
      }

      if (filter.sort_by) queryParams.append("sort_by", filter.sort_by);
      if (filter.sort_type)
        queryParams.append("sort_type", filter.sort_type.toString());
      queryParams.append("active", filter.active === false ? "false" : "true");

      const response = await getAPI(`/v5/state/list?${queryParams.toString()}`);

      return response.result as PaginatedAPIResponse<State>;
    } catch (error) {
      throw error;
    }
  };

  getAddressById = async (id: number) => {
    try {
      const response = await getAPI(`/v5/address/${id}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getDefaultBillFromAddress = async (customer_id: number) => {
    try {
      const response = await getAPI(
        `/v5/address/bill-from-address/default?customer_id=${customer_id}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getAllAddressesWithoutPagination = async (
    filter: AddressFilters
  ): Promise<Address[]> => {
    try {
      let getAddressUrl = "/v5/address/getAllAddresses";

      const queryParams = [];
      if (filter.customer_id) {
        queryParams.push(`customer_id=${filter.customer_id}`);
      }

      if (filter.active) {
        queryParams.push(`active=${filter.active}`);
      }

      if (filter.search_text) {
        queryParams.push(`search_text=${filter.search_text}`);
      }

      if (queryParams.length > 0) {
        getAddressUrl += `?${queryParams.join("&")}`;
      }

      const response = await getAPI(getAddressUrl);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getAddresses = async (
    filters: AddressFilters
  ): Promise<PaginatedAPIResponse<Address>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no !== undefined)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit !== undefined)
        queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.sort_by) queryParams.append("sort_by", filters.sort_by);

      if (filters.sort_type) queryParams.append("sort_type", filters.sort_type);

      if (filters.customer_id)
        queryParams.append("customer_id", filters.customer_id.toString());

      const response = await getAPI(
        `/v5/address/getAddress?${queryParams.toString()}`
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createCity = async (cityData: {
    cityName: string;
    stateId?: number;
    stateName?: string;
    country_id: number;
  }): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/admin/address/add-city`, cityData);
      return response;
    } catch (error) {
      throw error;
    }
  };
}
